import { z } from 'zod';
/**
 * The experience plane (Constitution §9).
 *
 * "Two planes: the data plane is shared and never forks; the experience plane
 * (workflows, features, views) may be personalized per user."
 *
 * The whole safety of §9 rests on one thing: personalising an experience must
 * not be able to fork data. That is not a rule anyone can be asked to
 * remember, so it is a shape. This schema describes what an experience *is* —
 * which views exist, in what order, with which columns shown, which steps a
 * workflow has, what someone's preferences are — and it has no member that
 * could name a table, a row, a query or a filter over real data.
 *
 * A person can have their own view of an order. They cannot have their own
 * order. The second sentence is true because there is nowhere to write it.
 *
 * `columns` is the closest this comes to naming data, and it names *fields to
 * show*, on an entity a spec already declared. Hiding a column is a view
 * preference; it is not a different value.
 */
const identifier = z.string().regex(/^[a-z][a-z_0-9]*$/);
export const viewPreferenceSchema = z.object({
    /** The entity this view is of. Declared by a spec; not created here. */
    entity: z.string().regex(/^[A-Z][A-Za-z0-9]*$/),
    /** Fields to show, in order. A field a spec did not declare is ignored. */
    columns: z.array(identifier).default([]),
    /** Field to sort by, and which way. */
    sortBy: identifier.optional(),
    sortDescending: z.boolean().default(false),
    /** Rows per page. Bounded, because an unbounded page is a denial of service. */
    pageSize: z.number().int().min(5).max(200).default(25),
});
export const workflowStepSchema = z.object({
    /** A spec id this step opens, or a view name. Never a command. */
    opens: z.string().min(1).max(120),
    label: z.string().min(1).max(120),
});
export const workflowPreferenceSchema = z.object({
    label: z.string().min(1).max(120),
    steps: z.array(workflowStepSchema).max(20).default([]),
    /** Pinned to the top of the person's workspace. */
    pinned: z.boolean().default(false),
});
/**
 * A capability someone built for themselves.
 *
 * This is what promotion is about: §9 says the system "reviews user- and
 * department-built capabilities and proposes the best ones". A capability
 * here is a named arrangement of views and workflows — the thing that made
 * one person's job easier and might make everyone's easier.
 */
export const capabilitySchema = z.object({
    label: z.string().min(1).max(120),
    description: z.string().max(500).default(''),
    views: z.array(z.string().min(1).max(120)).default([]),
    workflows: z.array(z.string().min(1).max(120)).default([]),
});
export const experiencePayloadSchema = z
    .object({
    views: z.record(z.string().min(1).max(120), viewPreferenceSchema).default({}),
    workflows: z.record(z.string().min(1).max(120), workflowPreferenceSchema).default({}),
    capabilities: z.record(z.string().min(1).max(120), capabilitySchema).default({}),
    /** Density, theme, locale. Nothing that changes what a number means. */
    preferences: z
        .record(z.string().min(1).max(60), z.union([z.string().max(200), z.boolean(), z.number()]))
        .default({}),
})
    // Strict, and that is the enforcement: an unknown key is refused rather
    // than carried, so nothing can be smuggled into the payload for something
    // downstream to interpret later.
    .strict();
export const EMPTY_EXPERIENCE = {
    views: {},
    workflows: {},
    capabilities: {},
    preferences: {},
};
/**
 * A person's experience: the baseline, with their own line applied over it.
 *
 * Key by key, not deep-merged arbitrarily. A person who reorders the columns
 * of one view has said nothing about any other view, and a merge that treated
 * their whole `views` object as a replacement would silently hide every
 * baseline view they had not touched.
 */
export function resolveExperience(baseline, personal) {
    return {
        views: { ...baseline.views, ...personal.views },
        workflows: { ...baseline.workflows, ...personal.workflows },
        capabilities: { ...baseline.capabilities, ...personal.capabilities },
        preferences: { ...baseline.preferences, ...personal.preferences },
    };
}
//# sourceMappingURL=experience.js.map